'use client';

import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { ShoppingBag } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import styles from './CategoryMenuPage.module.css';

/* ─── Types ──────────────────────────────────────────────────────────────── */

type MenuCategoryId = 'entrees' | 'plats' | 'desserts' | 'boissons' | 'menu-du-jour';

type CategoryNavItem = {
  id: MenuCategoryId;
  label: string;
};

type MenuItem = {
  key: string;
  name: string;
  description: string;
  dosage?: string;
  hidePrice?: boolean;
  price: number;
  badge?: string;
  badgeTone?: 'secondary' | 'primary' | 'neutral' | 'tertiary';
};

type MenuSection = {
  id: string;
  eyebrow?: string;
  title: string;
  intro?: string;
  items: MenuItem[];
};

type CrossLink = {
  id: MenuCategoryId;
  eyebrow: string;
  title: string;
};

type CategoryPageData = {
  title: string;
  subtitle: string;
  quote: string;
  sections: MenuSection[];
  highlight?: {
    badge: string;
    title: string;
    description: string;
  };
  takeaway: {
    title: string;
    description: string;
    action: string;
  };
  crossLinks: CrossLink[];
};

interface Props {
  locale: string;
  category: MenuCategoryId;
  data: CategoryPageData;
  navItems: CategoryNavItem[];
  navBrand: string;
  navReservations: string;
  navCartLabel: string;
  showTakeawayCta: boolean;
  heroEyebrow: string;
  introAriaLabel: string;
  menuListAriaLabel: string;
  crossNavAriaLabel: string;
  mobileNavAriaLabel: string;
  categoryNavAriaLabel: string;
  taxNotice: string;
}

/* ─── Constants ──────────────────────────────────────────────────────────── */

const EASE = [0.25, 0.46, 0.45, 0.94] as const;
const EASE_EXPO = [0.16, 1, 0.3, 1] as const;

const CATEGORY_ICONS: Record<MenuCategoryId, string> = {
  'menu-du-jour': 'today',
  entrees: 'restaurant_menu',
  plats: 'dinner_dining',
  desserts: 'icecream',
  boissons: 'wine_bar',
};

function formatPrice(locale: string, price: number) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(price);
}

/* ─── Hero ───────────────────────────────────────────────────────────────── */

function CategoryHero({
  title,
  subtitle,
  eyebrow,
}: {
  title: string;
  subtitle: string;
  eyebrow: string;
}) {
  const words = title.split(' ');

  return (
    <header className={styles.hero}>
      <motion.span
        className={styles.eyebrow}
        initial={{ y: 10 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.55, ease: 'easeOut' }}
      >
        {eyebrow}
      </motion.span>

      {/* Each word slides up from behind an overflow:hidden mask — editorial clip reveal */}
      <h1 aria-label={title} className={styles.heroTitle}>
        {words.map((word, i) => (
          <span key={i} className={styles.heroWordWrap}>
            <motion.span
              className={styles.heroWord}
              initial={{ y: 12 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.8, delay: 0.18 + i * 0.1, ease: EASE_EXPO }}
            >
              {word}
            </motion.span>
            {i < words.length - 1 ? '\u00A0' : ''}
          </span>
        ))}
      </h1>

      <motion.div
        className={styles.heroRule}
        aria-hidden="true"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.85, delay: 0.62, ease: EASE }}
      />

      <motion.p
        initial={{ y: 14 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.65, delay: 0.74, ease: 'easeOut' }}
      >
        {subtitle}
      </motion.p>
    </header>
  );
}

/* ─── Editorial intro ────────────────────────────────────────────────────── */

function EditorialIntro({ quote, ariaLabel }: { quote: string; ariaLabel: string }) {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section ref={ref} className={styles.editorialIntro} aria-label={ariaLabel}>
      <motion.span
        className={styles.editorialDeco}
        aria-hidden="true"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 0.65, ease: EASE }}
      />
      <motion.p
        initial={{ y: 22 }}
        animate={isInView ? { y: 0 } : {}}
        transition={{ duration: 0.85, delay: 0.12, ease: EASE_EXPO }}
      >
        {quote}
      </motion.p>
      <motion.span
        className={styles.editorialDeco}
        aria-hidden="true"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 0.65, delay: 0.2, ease: EASE }}
      />
    </section>
  );
}

/* ─── Animated menu item ─────────────────────────────────────────────────── */

function AnimatedMenuItemRow({
  item,
  locale,
  index,
}: {
  item: MenuItem;
  locale: string;
  index: number;
}) {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-16px' });

  return (
    <motion.article
      ref={ref}
      className={styles.menuItem}
      initial={{ opacity: 0, y: 16 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.065, ease: EASE }}
    >
      {/* Left accent line — reveals on hover via CSS */}
      <span className={styles.itemAccent} aria-hidden="true" />

      <div className={styles.itemHeader}>
        <div className={styles.itemNameGroup}>
          <h2>{item.name}</h2>
          {item.badge && (
            <span className={`${styles.itemBadge} ${styles[item.badgeTone ?? 'neutral']}`}>
              {item.badge}
            </span>
          )}
        </div>
        <div className={styles.itemPricing}>
          {item.dosage && <span className={styles.itemDosage}>{item.dosage}</span>}
          {!item.hidePrice && (
            <span className={styles.itemPrice}>
              {formatPrice(locale, item.price)}<sup className={styles.priceAsterisk}>*</sup>
            </span>
          )}
        </div>
      </div>
      <p>{item.description}</p>
    </motion.article>
  );
}

function MenuSectionBlock({
  section,
  locale,
}: {
  section: MenuSection;
  locale: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-24px' });

  return (
    <section ref={ref} className={styles.menuSectionBlock}>
      <motion.div
        className={styles.sectionHeading}
        initial={{ opacity: 0, y: 14 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, ease: EASE }}
      >
        {section.eyebrow && <span className={styles.sectionEyebrow}>{section.eyebrow}</span>}
        <h2>{section.title}</h2>
        {section.intro && <p>{section.intro}</p>}
      </motion.div>

      <div className={styles.itemList}>
        {section.items.map((item, index) => (
          <AnimatedMenuItemRow key={item.key} item={item} locale={locale} index={index} />
        ))}
      </div>
    </section>
  );
}

/* ─── TTC notice ─────────────────────────────────────────────────────────── */

function TTCNotice({ text }: { text: string }) {
  return (
    <motion.div
      className={styles.ttcNotice}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-24px' }}
      transition={{ duration: 0.65, ease: 'easeOut' }}
    >
      <span className={styles.ttcDeco} aria-hidden="true" />
      <p className={styles.ttcText}>
        <sup className={styles.ttcSup}>*</sup>
        {text}
      </p>
    </motion.div>
  );
}

/* ─── Highlight aside ───────────────────────────────────────────────────── */

function HighlightAside({
  highlight,
}: {
  highlight?: CategoryPageData['highlight'];
}) {
  const asideRef = useRef<HTMLElement>(null);
  const isInView = useInView(asideRef, { once: true, margin: '-60px' });

  if (!highlight) return null;

  return (
    <aside ref={asideRef} className={styles.highlight} aria-label={highlight.badge}>
      <motion.div
        className={styles.highlightCard}
        initial={{ opacity: 0, y: 18 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: EASE }}
      >
        <span>{highlight.badge}</span>
        <h2>{highlight.title}</h2>
        <p>{highlight.description}</p>
      </motion.div>
    </aside>
  );
}

/* ─── Takeaway band ──────────────────────────────────────────────────────── */

function TakeawayBand({ takeaway }: { takeaway: CategoryPageData['takeaway'] }) {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} className={styles.takeaway}>
      <motion.div
        className={styles.takeawayInner}
        initial={{ opacity: 0, y: 26 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.75, ease: EASE }}
      >
        <motion.span
          className={styles.takeawayDeco}
          aria-hidden="true"
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
        />
        <h2>{takeaway.title}</h2>
        <p>{takeaway.description}</p>
        <Link href="/commande-a-emporter" className={styles.takeawayButton}>
          {takeaway.action}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M3 8h10M9 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Link>
      </motion.div>
    </section>
  );
}

/* ─── Cross navigation ───────────────────────────────────────────────────── */

function CrossNavigation({
  crossLinks,
  ariaLabel,
}: {
  crossLinks: CrossLink[];
  ariaLabel: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <nav ref={ref} className={styles.crossNavigation} aria-label={ariaLabel}>
      <div className={styles.crossGrid}>
        {crossLinks.map((item, i) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 22 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
          >
            <Link
              href={{ pathname: '/menu/[category]', params: { category: item.id } }}
              className={styles.crossLink}
            >
              <span className={styles.crossEyebrow}>{item.eyebrow}</span>
              <strong>{item.title}</strong>
              <span className={styles.crossRule} aria-hidden="true" />
            </Link>
          </motion.div>
        ))}
      </div>
    </nav>
  );
}

/* ─── Main export ────────────────────────────────────────────────────────── */

export function CategoryMenuPageClient({
  locale,
  category,
  data,
  navItems,
  navBrand,
  navReservations,
  navCartLabel,
  showTakeawayCta,
  heroEyebrow,
  introAriaLabel,
  menuListAriaLabel,
  crossNavAriaLabel,
  mobileNavAriaLabel,
  categoryNavAriaLabel,
  taxNotice,
}: Props) {
  const hasHighlight = Boolean(data.highlight);

  return (
    <div className={styles.page}>
      {/* ── Sub-nav ── */}
      <nav className={styles.categoryNavBar} aria-label={categoryNavAriaLabel}>
        <div className={styles.categoryNavInner}>
          <Link href="/menu" className={styles.menuBrand}>
            {navBrand}
          </Link>

          <div className={styles.categoryLinks}>
            {navItems.map((item) => (
              <Link
                key={item.id}
                href={{ pathname: '/menu/[category]', params: { category: item.id } }}
                className={`${styles.categoryLink} ${item.id === category ? styles.categoryLinkActive : ''}`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className={styles.navActions}>
            <Link href="/contact" className={styles.reservationLink}>
              {navReservations}
            </Link>
            {showTakeawayCta && (
              <Link href="/commande-a-emporter" className={styles.bagLink} aria-label={navCartLabel}>
                <ShoppingBag className={styles.bagIcon} aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <CategoryHero title={data.title} subtitle={data.subtitle} eyebrow={heroEyebrow} />

      {/* ── Quote ── */}
      <EditorialIntro quote={data.quote} ariaLabel={introAriaLabel} />

      {/* ── Menu list + highlight ── */}
      <section
        className={`${styles.menuLayout} ${!hasHighlight ? styles.menuLayoutSingleColumn : ''}`}
        aria-label={menuListAriaLabel}
      >
        <div className={styles.sectionsList}>
          {data.sections.map((section) => (
            <MenuSectionBlock key={section.id} section={section} locale={locale} />
          ))}
          <TTCNotice text={taxNotice} />
        </div>

        <HighlightAside highlight={data.highlight} />
      </section>

      {/* ── Takeaway ── */}
      {showTakeawayCta && <TakeawayBand takeaway={data.takeaway} />}

      {/* ── Cross navigation ── */}
      <CrossNavigation crossLinks={data.crossLinks} ariaLabel={crossNavAriaLabel} />

      {/* ── Mobile bottom nav ── */}
      <nav className={styles.mobileBottomNav} aria-label={mobileNavAriaLabel}>
        {navItems.map((item) => (
          <Link
            key={item.id}
            href={{ pathname: '/menu/[category]', params: { category: item.id } }}
            className={`${styles.mobileBottomLink} ${item.id === category ? styles.mobileBottomLinkActive : ''}`}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              {CATEGORY_ICONS[item.id]}
            </span>
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}
